import { jsxs, jsx } from "react/jsx-runtime";
import { useClassNameFactory, useColorScheme, useElementSize } from "@contember/react-utils";
import { deprecate, fallback, themeClassName, colorSchemeClassName, omit, dataAttribute, controlsThemeClassName, px } from "@contember/utilities";
import { memo, useRef, useMemo } from "react";
import { ErrorList } from "../../ErrorList/ErrorList.js";
import { Label } from "../../Typography/Label.js";
import { Description } from "../../Typography/Description.js";
import { Stack } from "../../Stack/Stack.js";
const FieldContainer = memo(
  ({
    children,
    className: classNameProp,
    componentClassName = "field-container",
    description,
    errors,
    direction,
    display = "block",
    footer,
    evenly,
    gap = "gap",
    horizontal = false,
    label,
    labelDescription,
    labelPosition = "top",
    required,
    reverse,
    size,
    style,
    useLabelElement = true,
    width = "column",
    ...props
  }) => {
    deprecate("1.3.0", labelPosition === "labelInlineLeft", '`labelPosition="labelInlineLeft"`', '`labelPosition="left" display="inline"`');
    deprecate("1.3.0", labelPosition === "labelInlineRight", '`labelPosition="labelInlineRight"`', '`labelPosition="right" display="inline"`');
    deprecate("1.3.0", labelPosition === "labelLeft", '`labelPosition="labelLeft"`', '`labelPosition="left"`');
    deprecate("1.3.0", labelPosition === "labelRight", '`labelPosition="labelRight"`', '`labelPosition="right"`');
    display = fallback(display, labelPosition === "labelInlineLeft" || labelPosition === "labelInlineRight", "inline");
    labelPosition = fallback(labelPosition, labelPosition === "labelInlineLeft" || labelPosition === "labelLeft", "left");
    labelPosition = fallback(labelPosition, labelPosition === "labelInlineRight" || labelPosition === "labelRight", "right");
    const LabelElement = useLabelElement ? "label" : "div";
    const className = useClassNameFactory(componentClassName);
    const invalid = !!errors?.length;
    const asteriskClassName = [...themeClassName("danger"), colorSchemeClassName(useColorScheme())];
    const bodyContentRef = useRef(null);
    const { height } = useElementSize(bodyContentRef);
    const rest = omit(props, unfilteredBindingRestProps);
    return /* @__PURE__ */ jsxs(
      LabelElement,
      {
        ...rest,
        "data-invalid": dataAttribute(invalid),
        "data-size": dataAttribute(size),
        "data-label-position": dataAttribute(labelPosition),
        "data-display": dataAttribute(display),
        className: className(null, [
          invalid ? controlsThemeClassName("danger") : null,
          colorSchemeClassName(useColorScheme()),
          classNameProp
        ]),
        style: useMemo(() => labelPosition === "left" || labelPosition === "right" ? {
          "--cui-field-container--body-content-height": px(height),
          ...style
        } : style, [height, labelPosition, style]),
        children: [
          (label || labelDescription) && /* @__PURE__ */ jsxs("span", { className: className("header"), children: [
            label && /* @__PURE__ */ jsxs(Label, { children: [
              label,
              required && /* @__PURE__ */ jsx("span", { className: className("required-asterisk", asteriskClassName), children: "*" })
            ] }),
            labelDescription && /* @__PURE__ */ jsx(Description, { children: labelDescription })
          ] }),
          (children || description) && /* @__PURE__ */ jsxs("div", { className: className("body"), children: [
            children && /* @__PURE__ */ jsx(
              Stack,
              {
                ref: bodyContentRef,
                className: className("body-content"),
                direction,
                horizontal,
                evenly,
                reverse,
                gap,
                children
              }
            ),
            description && /* @__PURE__ */ jsx("span", { className: className("body-content-description"), children: description }),
            !!errors && errors.length > 0 && /* @__PURE__ */ jsx("div", { className: className("errors"), children: /* @__PURE__ */ jsx(ErrorList, { errors }) })
          ] }),
          footer && /* @__PURE__ */ jsx("div", { className: className("footer"), children: footer })
        ]
      }
    );
  }
);
FieldContainer.displayName = "FieldContainer";
const unfilteredBindingRestProps = [
  "connectingEntityField",
  "containerComponentExtraProps",
  "createNewForm",
  "currentValue",
  "enableRemoving",
  "initialEntityCount",
  "itemComponent",
  "itemComponentExtraProps",
  "lazy",
  "onClear",
  "onSearch",
  "optionLabel",
  "orderBy",
  "removalType",
  "searchByFields",
  "sortableBy"
];
export {
  FieldContainer
};
//# sourceMappingURL=FieldContainer.js.map
